import React from 'react';
import { Tag } from 'antd';

interface BadgeConfig {
  label: string;
  /** Hardcoded to antd's *default*-theme `success`/`processing` Tag palette (green-1/green-3/green-7
   * and blue-1/blue-3/blue-7) — same values the shared `EnvironmentBadge` in `libs/shared/ui`
   * (MJ-Monorepo) renders, since that app never overrides `colorInfo`/`colorSuccess`. Deliberately
   * NOT antd's `color="success"`/`color="processing"` props here: this app's `ConfigProvider`
   * sets `colorInfo: '#1890ff'` (`App.tsx`), which would recolor the whole "processing" preset
   * (background/border/text) off that seed instead of antd's default `#1677ff`. */
  lightBackground: string;
  lightBorder: string;
  lightColor: string;
  darkBackground: string;
  darkColor: string;
}

/** Only non-production Vite modes get a badge — `production` is intentionally absent
 * so it disappears once a build is production-facing. Keyed by `import.meta.env.MODE`
 * (`development` for `npm run dev` / `npm run builddev`, `staging` for `npm run
 * buildstaging`, `uat` for `npm run builduat`, `production` for `npm run buildprod`).
 * Each mode carries two tints: `light` for white/pale navbars, `dark` (translucent, no
 * border, same hue family) for the navy `bg-colorPrimary` navbars. */
const BADGE_CONFIG: Record<string, BadgeConfig> = {
  development: {
    label: 'DOGMA QA',
    lightBackground: '#fffbe6',
    lightBorder: '#ffe58f',
    lightColor: '#d48806',
    darkBackground: 'rgba(250, 173, 20, 0.25)',
    darkColor: '#ffe58f',
  },
  staging: {
    label: 'QA',
    lightBackground: '#f6ffed',
    lightBorder: '#b7eb8f',
    lightColor: '#52c41a',
    darkBackground: 'rgba(82, 196, 26, 0.25)',
    darkColor: '#b7eb8f',
  },
  uat: {
    label: 'UAT',
    lightBackground: '#e6f4ff',
    lightBorder: '#91caff',
    lightColor: '#1677ff',
    darkBackground: 'rgba(255, 255, 255, 0.18)',
    darkColor: '#e6f4ff',
  },
};

interface EnvironmentBadgeProps {
  /** `import.meta.env.MODE`. Modes with no matching entry above (`production`, or anything else) render nothing. */
  mode: string;
  /** Tint to match the navbar behind it — `light` (default) for white headers, `dark` for the
   * navy `bg-colorPrimary` ones, where a light-tint fill+border would clash. */
  variant?: 'light' | 'dark';
}

/** Small pill shown next to a navbar's title so a tester always knows which non-production
 * environment they're on. */
export const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({ mode, variant = 'light' }) => {
  const config = BADGE_CONFIG[mode];
  if (!config) return null;

  const isDark = variant === 'dark';

  return (
    <Tag
      bordered={!isDark}
      style={{
        margin: 0,
        background: isDark ? config.darkBackground : config.lightBackground,
        color: isDark ? config.darkColor : config.lightColor,
        ...(isDark ? {} : { borderColor: config.lightBorder }),
        fontWeight: 600,
        fontSize: 11,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
        lineHeight: '20px',
        padding: '0 10px',
      }}
    >
      {`${config.label.toUpperCase()} INSTANCE`}
    </Tag>
  );
};
